import { requestBindPayment } from '@chargespot/spjs'

import { ResponseError } from '@/utils/error'
import {
  getStorageItem,
  removeStorageItem,
  setStorageItem,
} from '@/utils/storage'

const PLAN_PAYMENT_BIND_REDIRECT_KEY = 'planPaymentBindRedirect'
export const PLAN_PAYMENT_BIND_REDIRECT_PATH = '/plan'
const SHARE_SPOT_PAYMENT_STATUS_INVALID_MESSAGE =
  'share spot payment status is invalid'

type PlanPaymentBindRedirect = {
  path: typeof PLAN_PAYMENT_BIND_REDIRECT_PATH
}

type Toast = (content: string) => void

type BindSpotPaymentOptions = {
  toast: Toast
  savePlanRedirect?: boolean
}

export function isSpotPaymentNotBindError(error: unknown) {
  const responseError = error as Partial<ResponseError> | null | undefined
  return Boolean(
    responseError?.errors?.some(
      (fieldError) =>
        fieldError.resource === 'SpotAppPayment' &&
        fieldError.code === 'spot_payment_not_bind'
    )
  )
}

export function isShareSpotPaymentStatusInvalidError(error: unknown) {
  const responseError = error as Partial<ResponseError> | null | undefined
  return Boolean(
    responseError?.message === SHARE_SPOT_PAYMENT_STATUS_INVALID_MESSAGE ||
      responseError?.errors?.some(
        (fieldError) =>
          fieldError.resource === 'ShareSpotUser' &&
          fieldError.code === 'payment_failed' &&
          responseError.message === SHARE_SPOT_PAYMENT_STATUS_INVALID_MESSAGE
      )
  )
}

export function shouldBindSpotPayment(error: unknown) {
  return (
    isSpotPaymentNotBindError(error) ||
    isShareSpotPaymentStatusInvalidError(error)
  )
}

export function setPlanPaymentBindRedirect() {
  setStorageItem<PlanPaymentBindRedirect>(PLAN_PAYMENT_BIND_REDIRECT_KEY, {
    path: PLAN_PAYMENT_BIND_REDIRECT_PATH,
  })
}

export function getPlanPaymentBindRedirect() {
  const redirect = getStorageItem<PlanPaymentBindRedirect>(
    PLAN_PAYMENT_BIND_REDIRECT_KEY
  )

  if (redirect?.path !== PLAN_PAYMENT_BIND_REDIRECT_PATH) return null
  return redirect
}

export function removePlanPaymentBindRedirect() {
  removeStorageItem(PLAN_PAYMENT_BIND_REDIRECT_KEY)
}

export async function bindSpotPayment(options: BindSpotPaymentOptions) {
  const { toast, savePlanRedirect = false } = options

  if (savePlanRedirect) {
    setPlanPaymentBindRedirect()
  }

  try {
    await requestBindPayment()
    if (savePlanRedirect) {
      removePlanPaymentBindRedirect()
    }
    return true
  } catch (e: any) {
    if (savePlanRedirect) {
      removePlanPaymentBindRedirect()
    }
    if (e?.errCode !== -1) toast(e?.errMsg)
    return false
  }
}

export async function requestWithSpotPaymentBindRetry<T>(
  request: () => Promise<T>,
  options: BindSpotPaymentOptions
) {
  try {
    return await request()
  } catch (e) {
    if (!shouldBindSpotPayment(e)) {
      return Promise.reject(e)
    }

    const isPaymentBound = await bindSpotPayment(options)
    if (!isPaymentBound) {
      return Promise.reject(e)
    }

    return request()
  }
}
